import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Label1, ToggleButtonGroup } from '@sainsburys-tech/fable'

import FulfilmentOptionButton from '../FulfilmentOptionButton/FulfilmentOptionButton'
import FulfilmentContent from '../FulfilmentContent/FulfilmentContent'
import { FULFILMENT_TYPES, getAvailabilityMessage, getFulfilmentOption } from '../fulfilmentBoxUtils'

export const FulfilmentBoxV2 = ({
  collectable,
  collectionMessage,
  collectionMessageKey,
  collectionIcon,
  collectionStore,
  deliverable,
  deliveryMessage,
  deliveryMessageKey,
  deliveryIcon,
  deliveryPostcode,
  openStoreSelector,
  onShowDeliveryForm,
  deliveryPostcodeForm = null,
  isLoading,
  collectionAdditionalContent = null,
  deliveryAdditionalContent = null,
  supplierInfoCTA = null
}) => {
  // If showing delivery input form, don't show previous availability in delivery option button
  const deliveryOptionKey = deliveryPostcodeForm ? null : deliveryMessageKey
  const deliveryOptionIcon = deliveryPostcodeForm ? null : deliveryIcon

  const fulfilmentOptions = [
    {
      type: FULFILMENT_TYPES.COLLECTION,
      ...getFulfilmentOption(collectionMessageKey, collectionIcon, !collectable),
      isDisabled: !collectable
    },
    {
      type: FULFILMENT_TYPES.DELIVERY,
      ...getFulfilmentOption(deliveryOptionKey, deliveryOptionIcon, !deliverable),
      isDisabled: !deliverable
    }
  ]

  const fulfilmentContent = [
    {
      type: FULFILMENT_TYPES.COLLECTION,
      isValidFulfilment: collectable,
      message: getAvailabilityMessage(collectionMessage, collectionStore),
      location: collectionStore,
      ctaCopy: 'Change store',
      onCtaClick: openStoreSelector,
      additionalContent: collectionAdditionalContent
    },
    {
      type: FULFILMENT_TYPES.DELIVERY,
      isValidFulfilment: deliverable,
      message: getAvailabilityMessage(deliveryMessage, deliveryPostcode, deliveryMessageKey),
      location: deliveryPostcode,
      ctaCopy: 'Change postcode',
      onCtaClick: onShowDeliveryForm,
      additionalContent: deliveryAdditionalContent,
      supplierInfoCTA,
      deliveryPostcodeForm
    }
  ]

  const [selectedType, setSelectedType] = useState(fulfilmentOptions.find(option => !option.isDisabled)?.type)

  useEffect(() => {
    const selectedOption = fulfilmentOptions.find(option => option.type === selectedType)

    // If selectedType doesn't exist or is now disabled, select the first available option
    if (!selectedOption || selectedOption.isDisabled) {
      const firstAvailableOption = fulfilmentOptions.find(option => !option.isDisabled)
      setSelectedType(firstAvailableOption?.type)
    }
  }, [collectable, deliverable])

  const selectedFulfilmentContent = fulfilmentContent.find(option => option.type === selectedType)

  if (!collectable && !deliverable) {
    return null
  }

  return (
    <div className='fulfilment-box-v2'>
      <Label1 colour='monochrome-darker' className='ds-pb-3 ds-text-base'>
        How to get your item
      </Label1>
      <ToggleButtonGroup
        label='Toggle between collection and delivery options'
        value={selectedType}
        className='button-group ds-mb-3'
        onChange={setSelectedType}
      >
        {fulfilmentOptions.map((option, index) => (
          <FulfilmentOptionButton
            {...option}
            key={`${option.type}-${index}`}
            value={option.type}
            isActive={selectedType === option.type}
            isLoading={isLoading}
          />
        ))}
      </ToggleButtonGroup>
      <FulfilmentContent {...selectedFulfilmentContent} isLoading={isLoading} />
    </div>
  )
}

FulfilmentBoxV2.propTypes = {
  collectable: PropTypes.bool,
  collectionMessage: PropTypes.string,
  collectionMessageKey: PropTypes.string,
  collectionIcon: PropTypes.string,
  collectionStore: PropTypes.string,
  deliverable: PropTypes.bool,
  deliveryMessage: PropTypes.string,
  deliveryMessageKey: PropTypes.string,
  deliveryIcon: PropTypes.string,
  deliveryPostcode: PropTypes.string,
  openStoreSelector: PropTypes.func,
  onShowDeliveryForm: PropTypes.func,
  deliveryPostcodeForm: PropTypes.node,
  isLoading: PropTypes.bool,
  collectionAdditionalContent: PropTypes.node,
  deliveryAdditionalContent: PropTypes.node
}
